import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api';
import Icon from '../components/icons';
import { canOperate } from '../lib/roles';
import RoleBanner from '../components/RoleBanner';

const STARTERS = [
  {
    label: 'SEO content loop',
    text: 'Ship an SEO content loop: competitor gaps, one pillar article, and three social posts',
  },
  {
    label: 'B2B outbound',
    text: 'Build a B2B outbound sequence with email copy, ad variants, and a competitor brief',
  },
  {
    label: 'Launch kit',
    text: 'Create a product launch kit: landing page copy, email sequence, and social announcement posts',
  },
];

function MessageBody({ content }) {
  const lines = String(content || '').split('\n');
  return (
    <>
      {lines.map((line, li) => (
        <React.Fragment key={li}>
          {line}
          {li < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
}

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return '';
  }
}

function tasksFromDetail(tasks) {
  return (tasks || []).map((t) => ({
    order: t.step_order,
    task_id: t.task_id,
    skill_key: t.skill_key,
    title: t.title,
    agent_name: t.agent_name,
    status: t.status || t.run_status,
    approval_required: t.status === 'awaiting_approval',
  }));
}

export default function Goals() {
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState('');
  const [history, setHistory] = useState([]);
  const [activeGoalId, setActiveGoalId] = useState(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [messages, setMessages] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [started, setStarted] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const loadHistory = useCallback(async (wsId) => {
    if (!wsId) {
      setHistory([]);
      return;
    }
    try {
      const rows = await apiGet(`/goals?workspace_id=${Number(wsId)}&limit=40`);
      setHistory(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.warn('[goals] history load failed', e);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const wsRows = await apiGet('/workspaces');
      const spaces = Array.isArray(wsRows) ? wsRows : [];
      setWorkspaces(spaces);
      const preferred =
        spaces.find((w) => /globex main/i.test(w.name || '')) ||
        spaces.find((w) => /acme main/i.test(w.name || '')) ||
        spaces[0];
      const nextId = preferred ? String(preferred.id) : '';
      setWorkspaceId((prev) => prev || nextId);
      await loadHistory(preferred ? preferred.id : null);
    } catch (e) {
      setErr(String(e.message || e));
    }
  }, [loadHistory]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (workspaceId) loadHistory(workspaceId);
  }, [workspaceId, loadHistory]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, busy, lastResult]);

  function startNewChat() {
    setActiveGoalId(null);
    setMessages([]);
    setLastResult(null);
    setStarted(false);
    setErr('');
    setInput('');
    inputRef.current?.focus();
  }

  async function openGoal(goalId) {
    setErr('');
    setBusy(true);
    try {
      const detail = await apiGet(`/goals/${goalId}`);
      const msgs = (detail.messages || []).map((m) => ({
        role: m.role,
        content: m.content,
      }));
      setActiveGoalId(goalId);
      setMessages(msgs);
      setStarted(msgs.length > 0);
      setLastResult({
        goal_id: goalId,
        planner: detail.goal?.planner,
        summary: detail.goal?.summary,
        tasks: tasksFromDetail(detail.tasks),
      });
      if (detail.goal?.workspace_id) {
        setWorkspaceId(String(detail.goal.workspace_id));
      }
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function submitGoal(e) {
    e.preventDefault();
    setErr('');
    const goal = input.trim();
    if (!goal) return;
    if (!workspaceId) {
      setErr('Pick a workspace first.');
      return;
    }
    if (!canOperate()) {
      setErr('Operator role required to create goals.');
      return;
    }

    setStarted(true);
    setMessages((m) => [...m, { role: 'user', content: goal }]);
    setInput('');
    setBusy(true);

    try {
      let result;
      if (activeGoalId) {
        result = await apiPost(`/goals/${activeGoalId}/messages`, {
          content: goal,
          run_tasks: true,
          auto_route: true,
        });
        setMessages(
          (result.messages || []).map((m) => ({
            role: m.role,
            content: m.content,
          }))
        );
        setLastResult({
          goal_id: activeGoalId,
          planner: result.planner,
          summary: result.summary,
          tasks: result.tasks || [],
        });
      } else {
        result = await apiPost('/goals', {
          workspace_id: Number(workspaceId),
          goal,
          auto_route: true,
          run_skills: true,
          created_by: 'goals-chat',
        });
        setActiveGoalId(result.goal_id);
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content: result.reply || result.summary || 'Backlog created and agents routed.',
          },
        ]);
        setLastResult(result);
      }
      await loadHistory(workspaceId);
    } catch (e2) {
      const msg = String(e2.message || e2);
      setErr(msg);
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: `Couldn’t create that goal: ${msg}` },
      ]);
    } finally {
      setBusy(false);
    }
  }

  function useStarter(text) {
    setInput(text);
    inputRef.current?.focus();
  }

  const wsName =
    workspaces.find((w) => String(w.id) === String(workspaceId))?.name ||
    'this workspace';

  return (
    <main className="page goals-page">
      <div className="page__header page__header--split">
        <div className="page__header-row">
          <span className="page__icon"><Icon name="goals" /></span>
          <div className="page__header-text">
            <h2>Goals</h2>
            <p>
              Describe an outcome — we pick skills, route agents, enqueue runs, and sync the backlog to Plane.
              Chat history is saved per goal.
            </p>
          </div>
        </div>
        <div className="goals-header-actions">
          <label className="goals-ws">
            Workspace
            <select
              className="field-select"
              value={workspaceId}
              onChange={(e) => {
                setWorkspaceId(e.target.value);
                startNewChat();
              }}
              disabled={busy}
            >
              {workspaces.length === 0 && <option value="">No workspaces</option>}
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name || `Workspace ${w.id}`}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn-secondary" onClick={startNewChat} disabled={busy}>
            New chat
          </button>
        </div>
      </div>

      <RoleBanner action="create goals" />
      {err && <div className="status status--error">{err}</div>}

      <div className="goals-layout">
        <aside className="panel goals-history" aria-label="Chat history">
          <div className="goals-history__head">
            <h3>History</h3>
            <span>{history.length}</span>
          </div>
          {history.length === 0 ? (
            <p className="goals-history__empty">No saved chats yet for this workspace.</p>
          ) : (
            <ul className="goals-history__list">
              {history.map((g) => (
                <li key={g.id}>
                  <button
                    type="button"
                    className={`goals-history__item ${
                      Number(activeGoalId) === Number(g.id) ? 'is-active' : ''
                    }`}
                    onClick={() => openGoal(g.id)}
                    disabled={busy}
                  >
                    <span className="goals-history__title">
                      {g.title || g.preview || `Goal #${g.id}`}
                    </span>
                    <span className="goals-history__meta">
                      #{g.id} · {g.task_count || 0} tasks · {formatWhen(g.updated_at || g.created_at)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="panel goals-shell" aria-label="Goals conversation">
          <div className="goals-thread" aria-live="polite">
            {!started && (
              <div className="goals-empty">
                <p className="goals-empty__kicker">Loop · assign · backlog</p>
                <h3>What should {wsName} ship next?</h3>
                <p className="goals-empty__sub">
                  One message creates the skill loop and task backlog. Open a past chat from History anytime.
                </p>
                <div className="goals-starters">
                  {STARTERS.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      className="goals-starter"
                      disabled={busy || !canOperate()}
                      onClick={() => useStarter(s.text)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={`${m.role}-${i}`}
                className={`goals-msg goals-msg--${m.role === 'user' ? 'user' : 'assistant'}`}
              >
                <span className="goals-msg__who">
                  {m.role === 'user' ? 'You' : 'Planner'}
                </span>
                <div className="goals-msg__bubble">
                  <MessageBody content={m.content} />
                </div>
              </div>
            ))}

            {busy && (
              <div className="goals-msg goals-msg--assistant">
                <span className="goals-msg__who">Planner</span>
                <div className="goals-msg__bubble goals-msg__bubble--typing">
                  Planning & creating tasks…
                </div>
              </div>
            )}

            {lastResult && (lastResult.tasks || []).length > 0 && (
              <div className="goals-backlog">
                <div className="goals-backlog__head">
                  <h3>Goal #{lastResult.goal_id}</h3>
                  {lastResult.planner && (
                    <span className="goals-backlog__planner">{lastResult.planner}</span>
                  )}
                </div>
                {lastResult.summary && (
                  <p className="goals-backlog__summary">{lastResult.summary}</p>
                )}
                <ul className="goals-backlog__steps">
                  {(lastResult.tasks || []).map((t) => (
                    <li key={t.task_id || t.order}>
                      <strong>#{t.task_id}</strong>
                      <span>{t.title}</span>
                      <small>
                        {t.skill_key}
                        {t.agent_name ? ` · ${t.agent_name}` : ''}
                        {t.approval_required ? ' · approval' : ''}
                      </small>
                    </li>
                  ))}
                </ul>
                <div className="goals-backlog__actions">
                  <Link to="/tasks" className="btn-primary">Open Tasks</Link>
                  <Link to="/runs" className="btn-secondary">View Runs</Link>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {canOperate() ? (
            <form className="goals-composer" onSubmit={submitGoal}>
              <textarea
                ref={inputRef}
                className="goals-composer__input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!busy && input.trim()) submitGoal(e);
                  }
                }}
                rows={3}
                placeholder={
                  activeGoalId
                    ? 'Follow up on this goal — add more work…'
                    : 'Describe the outcome — not the tickets…'
                }
                disabled={busy}
                required
              />
              <div className="goals-composer__bar">
                <span className="goals-composer__hint">
                  {activeGoalId
                    ? `Continuing Goal #${activeGoalId} · Enter to send`
                    : 'Enter to send · Shift+Enter for line'}
                </span>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={busy || !input.trim()}
                >
                  {busy ? 'Routing…' : activeGoalId ? 'Add to goal' : 'Create backlog'}
                </button>
              </div>
            </form>
          ) : (
            <div className="status status--muted">
              View-only — ask an admin to upgrade you to Operator.
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
